import { brightenColor } from './color';
import { CONSTELLATION_DOT_DIAMETER, CONSTELLATION_SIZE, FULL_ROTATION_RADIANS } from './constants';
import { getTwgTravelingPulses, TWG_CONNECTIONS } from './poses/twg';
import type { ConstellationFrame } from './types';

const TWG_LINE_WIDTH = 0.9;
const TWG_DASH_LENGTH = 0.1;
const TWG_DASH_GAP = 3.2;
const TWG_DASH_SPEEDS = [5, 8, 11] as const;
const TWG_PULSE_HALO_RADIUS = 2.4;
const TWG_PULSE_CORE_RADIUS = 1.1;

const GLOW_FALLOFF_POWER = 1.5;
const MAXIMUM_GLOW_SPREAD = (CONSTELLATION_SIZE / 48) * 2.6;
const DEFAULT_GLOW_OPACITY = 0.42;
const GLOW_OPACITY_AT_CORE_EDGE = 0.3;
const BRIGHTNESS_GLINT_POWER = 4;
const MAXIMUM_BASE_BRIGHTNESS_BOOST = 0.3;
const MAXIMUM_GLOW_BRIGHTNESS_BOOST = 0.45;

type TwgPaintState = Readonly<{
	color: string;
	edgeOpacity: number;
	opacity: number;
	pulseHaloOpacity: number;
	timeSeconds: number;
}>;

type ConstellationPaintOptions = Readonly<{
	glowOpacity?: number;
	twg?: TwgPaintState;
}>;

const clamp = (value: number, min = 0, max = 1): number => Math.max(min, Math.min(max, value));

const paintTwgGraph = (
	context: CanvasRenderingContext2D,
	frame: ConstellationFrame,
	{ color, edgeOpacity, opacity, pulseHaloOpacity, timeSeconds }: TwgPaintState,
): void => {
	if (opacity <= 0) {
		return;
	}

	context.save();
	context.strokeStyle = color;
	context.lineCap = 'round';
	context.lineWidth = TWG_LINE_WIDTH;
	context.setLineDash([TWG_DASH_LENGTH, TWG_DASH_GAP]);

	TWG_CONNECTIONS.forEach(([fromIndex, toIndex], connectionIndex) => {
		const from = frame[fromIndex];
		const to = frame[toIndex];
		const direction = connectionIndex % 2 === 0 ? -1 : 1;
		const dashSpeed = TWG_DASH_SPEEDS[connectionIndex % TWG_DASH_SPEEDS.length];

		context.globalAlpha = opacity * edgeOpacity;
		context.lineDashOffset = direction * timeSeconds * dashSpeed;
		context.beginPath();
		context.moveTo(from.x, from.y);
		context.lineTo(to.x, to.y);
		context.stroke();
	});

	context.fillStyle = color;
	getTwgTravelingPulses(frame, timeSeconds).forEach(({ strength, x, y }) => {
		context.globalAlpha = opacity * strength * pulseHaloOpacity;
		context.beginPath();
		context.arc(x, y, TWG_PULSE_HALO_RADIUS, 0, FULL_ROTATION_RADIANS);
		context.fill();

		context.globalAlpha = opacity * strength;
		context.beginPath();
		context.arc(x, y, TWG_PULSE_CORE_RADIUS, 0, FULL_ROTATION_RADIANS);
		context.fill();
	});

	context.restore();
};

const paintDotGlow = (
	context: CanvasRenderingContext2D,
	dot: ConstellationFrame[number],
	radius: number,
	glowStrength: number,
	glowOpacity: number,
): void => {
	if (glowStrength <= 0 || glowOpacity <= 0) {
		return;
	}

	const glowRadius = radius + MAXIMUM_GLOW_SPREAD * glowStrength;
	const coreEdge = radius / glowRadius;
	const glow = context.createRadialGradient(dot.x, dot.y, 0, dot.x, dot.y, glowRadius);

	glow.addColorStop(0, 'rgba(255,255,255,1)');
	glow.addColorStop(coreEdge, `rgba(255,255,255,${GLOW_OPACITY_AT_CORE_EDGE})`);
	glow.addColorStop(1, 'rgba(255,255,255,0)');

	context.save();
	context.fillStyle = glow;
	context.globalAlpha = glowStrength * glowOpacity;
	context.beginPath();
	context.arc(dot.x, dot.y, glowRadius, 0, FULL_ROTATION_RADIANS);
	context.fill();
	context.restore();
};

const getVisualGlowStrength = (glowStrength: number): number =>
	Math.pow(clamp(glowStrength), GLOW_FALLOFF_POWER);

const getCoreBrightnessMultiplier = ({
	brightness,
	glowStrength,
}: ConstellationFrame[number]): number =>
	1 +
	Math.pow(clamp(brightness), BRIGHTNESS_GLINT_POWER) * MAXIMUM_BASE_BRIGHTNESS_BOOST +
	getVisualGlowStrength(glowStrength) * MAXIMUM_GLOW_BRIGHTNESS_BOOST;

const paintDot = (context: CanvasRenderingContext2D, dot: ConstellationFrame[number]): void => {
	const radius = (CONSTELLATION_DOT_DIAMETER / 2) * dot.scale;

	context.save();
	context.fillStyle = brightenColor(dot.color, getCoreBrightnessMultiplier(dot));
	context.beginPath();
	context.arc(dot.x, dot.y, radius, 0, FULL_ROTATION_RADIANS);
	context.fill();
	context.restore();
};

export const paintConstellationFrame = (
	context: CanvasRenderingContext2D,
	frame: ConstellationFrame,
	options: ConstellationPaintOptions = {},
): void => {
	context.clearRect(0, 0, CONSTELLATION_SIZE, CONSTELLATION_SIZE);

	if (options.twg) {
		paintTwgGraph(context, frame, options.twg);
	}

	frame.forEach((dot) => {
		const radius = (CONSTELLATION_DOT_DIAMETER / 2) * dot.scale;
		paintDotGlow(
			context,
			dot,
			radius,
			getVisualGlowStrength(dot.glowStrength),
			options.glowOpacity ?? DEFAULT_GLOW_OPACITY,
		);
	});
	frame.forEach((dot) => paintDot(context, dot));
};
